/** Target format for voice upload / ASR-friendly audio */
export const PCM_TARGET_SAMPLE_RATE = 16000;
export const PCM_WAV_MIME_TYPE = 'audio/wav';

/**
 * Linear downsample from Float32 mono to a target sample rate.
 * Matches the RecordRTC desiredSampRate approach used for 16 kHz capture.
 */
export const downsampleToRate = (
    input: Float32Array,
    inputSampleRate: number,
    outputSampleRate: number
): Float32Array => {
    if (outputSampleRate === inputSampleRate) {
        return input;
    }

    if (outputSampleRate > inputSampleRate) {
        throw new Error('Cannot upsample audio in downsampleToRate');
    }

    const sampleRateRatio = inputSampleRate / outputSampleRate;
    const newLength = Math.round(input.length / sampleRateRatio);
    const result = new Float32Array(newLength);
    let offsetResult = 0;
    let offsetBuffer = 0;

    while (offsetResult < result.length) {
        const nextOffsetBuffer = Math.round((offsetResult + 1) * sampleRateRatio);
        let accum = 0;
        let count = 0;

        for (let i = offsetBuffer; i < nextOffsetBuffer && i < input.length; i++) {
            accum += input[i];
            count++;
        }

        result[offsetResult] = count > 0 ? accum / count : 0;
        offsetResult++;
        offsetBuffer = nextOffsetBuffer;
    }

    return result;
};

/** Convert Float32 [-1, 1] samples to Int16 PCM */
export const floatTo16BitPCM = (input: Float32Array): Int16Array => {
    const output = new Int16Array(input.length);

    for (let i = 0; i < input.length; i++) {
        const sample = Math.max(-1, Math.min(1, input[i]));
        output[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
    }

    return output;
};

/** Mix interleaved/multi-channel Float32 into mono */
export const mixToMono = (input: Float32Array, channelCount: number): Float32Array => {
    if (channelCount <= 1) {
        return input;
    }

    const frameCount = Math.floor(input.length / channelCount);
    const mono = new Float32Array(frameCount);

    for (let i = 0; i < frameCount; i++) {
        let sum = 0;
        for (let channel = 0; channel < channelCount; channel++) {
            sum += input[i * channelCount + channel];
        }
        mono[i] = sum / channelCount;
    }

    return mono;
};

/**
 * Build a WAV Blob (RIFF) from Int16 mono PCM.
 * Sample rate should be 16000 for ASR-friendly uploads.
 */
export const encodeWavFromInt16 = (
    samples: Int16Array,
    sampleRate: number = PCM_TARGET_SAMPLE_RATE
): Blob => {
    const bytesPerSample = 2;
    const blockAlign = bytesPerSample; // mono
    const byteRate = sampleRate * blockAlign;
    const dataSize = samples.length * bytesPerSample;
    const buffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(buffer);

    const writeString = (offset: number, value: string) => {
        for (let i = 0; i < value.length; i++) {
            view.setUint8(offset + i, value.charCodeAt(i));
        }
    };

    writeString(0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true); // PCM fmt chunk size
    view.setUint16(20, 1, true); // PCM format
    view.setUint16(22, 1, true); // mono
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, 16, true); // bits per sample
    writeString(36, 'data');
    view.setUint32(40, dataSize, true);

    let offset = 44;
    for (let i = 0; i < samples.length; i++, offset += 2) {
        view.setInt16(offset, samples[i], true);
    }

    return new Blob([buffer], { type: PCM_WAV_MIME_TYPE });
};

/** Merge Int16 chunks into one contiguous buffer */
export const concatInt16Arrays = (chunks: Int16Array[]): Int16Array => {
    const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
    const result = new Int16Array(totalLength);
    let offset = 0;

    for (const chunk of chunks) {
        result.set(chunk, offset);
        offset += chunk.length;
    }

    return result;
};
